<template>
    <div class="flex flex-col gap-3 min-w-0">
        <div class="flex flex-row items-center gap-2">
            <div v-if="variant === ScheduleFollowUpVariant.DEFAULT" class="flex flex-col md:flex-row flex-wrap items-stretch md:items-center gap-2 body-small-semibold w-full">
                <BaseButton
                    theme="neutral"
                    :disabled="disabled"
                    class="flex items-center gap-3 grow min-w-0 md:basis-56 whitespace-normal"
                    type="button"
                    title="Edit date"
                    :aria-expanded="!collapsed"
                    @click="toggleCollapse">
                    <i class="bi bi-calendar text-xl mr-2" aria-hidden="true"></i>
                    <span>{{ formattedDate.weekday }}, {{ formattedDate.month }} {{ formattedDate.day }}</span>
                    <i class="inline md:hidden bi bi-pen text-lg ml-auto" aria-hidden="true"></i>
                </BaseButton>
                <BaseButton
                    theme="neutral"
                    :disabled="disabled"
                    class="flex items-center gap-3 grow min-w-0 md:basis-40 whitespace-normal"
                    type="button"
                    title="Edit time"
                    :aria-expanded="!collapsed"
                    @click="toggleCollapse">
                    <i class="bi bi-clock text-xl" aria-hidden="true"></i>
                    {{ formattedDate.time }}
                    <i class="inline md:hidden bi bi-pen text-lg ml-auto" aria-hidden="true"></i>
                </BaseButton>
                <BaseButton v-if="collapsed" :disabled="disabled" title="Edit date" theme="tertiary" icon-only class="hidden md:inline grow-0" @click="toggleCollapse">
                    <span role="presentation" class="bi bi-pen text-lg"></span>
                </BaseButton>
            </div>
            <BaseCard
                v-else-if="variant === ScheduleFollowUpVariant.FULL"
                as="button"
                type="button"
                padding="lg"
                :disabled="disabled"
                :aria-expanded="!collapsed"
                class="text-left w-full min-w-0 flex flex-col gap-3 disabled:cursor-not-allowed"
                @click="toggleCollapse">
                <i class="bi bi-calendar text-xl" aria-hidden="true"></i>
                <h2 class="title-body">{{ title }}</h2>
                <div class="body-bold">{{ formattedDate.month }} {{ formattedDate.day }} @ {{ formattedDate.time }}</div>
                <div class="body-semibold text-ink-muted">
                    {{ description }}
                </div>
            </BaseCard>
        </div>
        <BaseCard v-if="!collapsed" padding="sm" class="min-w-0 w-full">
            <BaseCalendar :model-value="selectedDate" :min-value="minValue" :disabled="disabled" surface="outlined" @update:model-value="onDateUpdate" />
            <div class="mt-3">
                <input
                    class="border border-control-border bg-control-bg text-control-text rounded-md p-2 focus-field disabled:bg-control-disabled-bg"
                    type="time"
                    aria-label="Time"
                    :disabled="disabled"
                    :value="selectedTime"
                    @change="onTimeUpdate" />
            </div>
        </BaseCard>
        <BaseCard padding="lg">
            <label :for="checkboxId" :class="disabled ? '' : 'cursor-pointer'" class="relative flex gap-4 justify-between items-center">
                <div class="text-left">
                    <div :id="`${checkboxId}-question`" class="body-small-bold">Weekly check-ins?</div>
                    <div :id="`${checkboxId}-weekday`" class="body-small text-ink-muted">Check-in with me on {{ formattedDate.weekday }}</div>
                </div>
                <BaseSwitch
                    :id="checkboxId"
                    :disabled="disabled"
                    :model-value="recurring"
                    :aria-labelledby="`${checkboxId}-question ${checkboxId}-weekday`"
                    class="shrink-0"
                    @update:model-value="onRecurringUpdate" />
            </label>
        </BaseCard>
    </div>
</template>

<script lang="ts">
const ScheduleFollowUpVariant = {
    DEFAULT: "default", // just date and time
    FULL: "full", // includes event title and description
};
export { ScheduleFollowUpVariant };
</script>
<script setup lang="ts">
import type { ZonedDateTime } from "@internationalized/date";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCalendar from "~vue/components/BaseCalendar.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseSwitch from "~vue/components/BaseSwitch.vue";
import { formatDate } from "~vue/utils/formatDate";
import { DateTime } from "luxon";
import { computed, ref, useId, watch } from "vue";

import { convertDateTimeToDateValue } from "../dateUtils";

type ScheduleFollowUpProps = {
    date?: Date;
    disabled?: boolean;
    recurring?: boolean;
    isCollapsed?: boolean;
    coachFollowupEvents?: Array<unknown>;
    variant?: string;
    title?: string;
    description?: string;
};

const {
    isCollapsed = true,
    date = new Date(Date.now()),
    disabled = false,
    recurring = true,
    variant = ScheduleFollowUpVariant.DEFAULT,
    title = "",
    description = "",
} = defineProps<ScheduleFollowUpProps>();

const checkboxId = useId();

const emit = defineEmits<{
    dateUpdate: [date: Date];
    recurringUpdate: [recurring: boolean];
}>();

const collapsed = ref(isCollapsed);

watch(
    () => isCollapsed,
    (value) => {
        collapsed.value = value;
    },
);

const luxonDate = () => DateTime.fromJSDate(date);

const formattedDate = computed(() => {
    const date = luxonDate();
    return {
        day: date.day,
        month: formatDate(date, "month"),
        weekday: formatDate(date, "weekday"),
        time: formatDate(date, "time"),
    };
});

const toggleCollapse = () => {
    if (disabled) {
        return;
    }
    collapsed.value = !collapsed.value;
};

const onDateUpdate = (date: ZonedDateTime) => {
    if (disabled) {
        return;
    }
    const dateTime = luxonDate();
    const updatedDate = date.set({ hour: dateTime.hour, minute: dateTime.minute, second: 0, millisecond: 0 });
    emit("dateUpdate", updatedDate.toDate());
};

const onTimeUpdate = (event: Event) => {
    if (disabled) {
        return;
    }
    const target = event.target as HTMLInputElement;
    const [hours, minutes] = target.value.split(":").map(Number);
    const updatedDate = luxonDate().set({ hour: hours, minute: minutes });
    emit("dateUpdate", updatedDate.toJSDate());
};

const onRecurringUpdate = () => {
    emit("recurringUpdate", !recurring);
};

const minValue = computed(() => {
    return convertDateTimeToDateValue(DateTime.now());
});

const selectedTime = computed(() => {
    return luxonDate().toFormat("HH:mm");
});

const selectedDate = computed(() => {
    return convertDateTimeToDateValue(luxonDate());
});
</script>
